import type { ChatMessage, WellFormedError } from '@elara/client-core/models/ChatMessageModel';
import type { MessageUiState } from '@elara/client-core/states/MessageUiState';
import type { ResourceMeta } from '@elara/core/agent/resources';
import type { RetryMetadata } from '@elara/domain/types';

import type { DebugUsageMetrics } from '#ui/chat/message-debug-metrics.js';
import type { BadgeVariant } from '#ui/index.js';

import { isNil } from 'lodash-es';

import { formatShortToolError } from '@elara/client-core/lib/errors';
import { cn } from '@elara/client-core/lib/utils';
import { isWellFormedError } from '@elara/client-core/models/ChatMessageModel';
import { extractResourceMeta } from '@elara/core/agent/resources';
import { SKILL_ROUTE_RESOURCE_PROVIDER } from '@elara/domain/schemas/skill-route-schema';

import { resolveDebugUsageMetrics } from '#ui/chat/message-debug-metrics.js';
import { describeSkillRouteLines, skillsInPrompt } from '#ui/chat/skill-route-groups.js';
import { renderWidget } from '#ui/chat/widget-renderer.jsx';
import { CopyButton } from '#ui/copy-button.jsx';
import { CollapsibleBlock } from '#ui/debug/collapsible-block.jsx';
import { CollapsibleDump } from '#ui/debug/collapsible-dump.jsx';
import {
  Badge,
  Button,
  Card,
  IconSizes,
  MaterialIcon,
  PrettyPre,
  PrettyStack,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '#ui/index.js';

interface MessageDebugStatusBarProps {
  state: MessageUiState;
  msg: ChatMessage;
  align?: 'start' | 'end';
  showDeleteMessageAction?: boolean;
  isDeleteMessageBusy?: boolean;
  onDeleteMessage?: () => void;
  showResendMessageAction?: boolean;
  isResendMessageBusy?: boolean;
  onResendMessage?: () => void;
}

export function MessageDebugStatusBar({
  state,
  msg,
  align = msg.isModelMessage ? 'start' : 'end',
  showDeleteMessageAction = false,
  isDeleteMessageBusy = false,
  onDeleteMessage,
  showResendMessageAction = false,
  isResendMessageBusy = false,
  onResendMessage,
}: MessageDebugStatusBarProps) {
  // The skill router's entry renders as the Skill Routing block instead of a resource card.
  const resources = (state.debugResourceMetadata ?? []).filter(
    (resource) => resource.provider !== SKILL_ROUTE_RESOURCE_PROVIDER,
  );
  const usageMetrics = resolveDebugUsageMetrics(msg.metadata);
  const shouldRenderDeleteAction = showDeleteMessageAction && onDeleteMessage;
  const shouldRenderResendAction = showResendMessageAction && onResendMessage;
  const isAnyMessageActionBusy = isDeleteMessageBusy || isResendMessageBusy;
  const retries = msg.metadata?.retries as RetryMetadata | undefined;
  const skillRoute = state.skillRoute;
  return (
    <div className={cn('mt-1 flex flex-col', align === 'start' ? 'items-start' : 'items-end')}>
      <div className="flex w-full items-center gap-1">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-5 gap-1 px-1.5 text-[10px] leading-none"
            onClick={() => state.toggleDebugPanel()}
          >
            {state.isDebugPanelExpanded ? (
              <MaterialIcon icon="expand_more" className="h-3.5 w-3.5" />
            ) : (
              <MaterialIcon icon="chevron_right" className="h-3.5 w-3.5" />
            )}
            Debug Info
          </Button>
          <CopyButton getContent={() => JSON.stringify(msg, null, 2)} size={'icon-sm'} variant={'icon'} />
          {shouldRenderDeleteAction && (
            <Button
              variant="icon"
              size="icon-sm"
              tooltip="Delete message"
              aria-label="Delete message"
              onClick={onDeleteMessage}
              isBusy={isDeleteMessageBusy}
              disabled={isAnyMessageActionBusy}
            >
              <MaterialIcon icon="delete" size="xxs" />
            </Button>
          )}
          {shouldRenderResendAction && (
            <Button
              variant="icon"
              size="icon-sm"
              tooltip="Resend message"
              aria-label="Resend message"
              onClick={onResendMessage}
              isBusy={isResendMessageBusy}
              disabled={isAnyMessageActionBusy}
            >
              <MaterialIcon icon="replay" size="xxs" />
            </Button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <DebugExternalLinks metrics={usageMetrics} />
          <DebugUsageBadges metrics={usageMetrics} messageCreatedAt={msg.createdAt} />
        </div>
      </div>

      {state.isDebugPanelExpanded && (
        <Card className="mt-2 p-3 bg-muted/50 text-xs font-mono overflow-hidden w-full">
          <div className="flex flex-col items-start overflow-x-auto">
            {msg.wellFormedErrors.map((error, idx) => (
              <WellFormedErrorDebugCard key={idx} error={error} />
            ))}

            {msg.unknownErrors.map((error, idx) => (
              <CollapsibleDump
                key={idx}
                icon={'error'}
                label="Errors"
                dump={error}
                simplified
                className="border-t bg-destructive/10"
                align={align}
              />
            ))}

            {retries?.attempts.map((retry, idx) => (
              <CollapsibleDump
                key={idx}
                icon={'error'}
                label={`Retry #${retry.attempt} (model: ${retry.modelId})`}
                dump={retry.error}
                simplified
                className="border-t bg-destructive/10"
                align={align}
              />
            ))}

            {skillRoute && (
              <CollapsibleBlock
                icon={'menu_book'}
                label={`Skill Routing (${skillsInPrompt(skillRoute).length} skills${skillRoute.transitions?.length ? ` · ${skillRoute.transitions.length} move${skillRoute.transitions.length === 1 ? '' : 's'}` : ''})`}
                initialOpen
                className="border-t"
                align={align}
              >
                <div className="ml-2 mb-2 flex flex-col gap-1" data-testid="skill-routing-block">
                  {describeSkillRouteLines(skillRoute).map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                  <CollapsibleDump label="Raw route" dump={skillRoute} icon={'info'} align={align} />
                </div>
              </CollapsibleBlock>
            )}

            {state.widgets.length > 0 && (
              <CollapsibleBlock
                icon={'build'}
                label={`Widgets (${state.widgets.length})`}
                className="border-t"
                align={align}
              >
                {state.widgets.map((widget, idx) => (
                  <div key={idx} className="ml-2 mb-5 flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      {widget.extendedStatus !== 'completed' && (
                        <Badge variant={widget.badgeVariant} className="text-[10px] h-4">
                          {widget.extendedStatus}
                        </Badge>
                      )}
                      <strong>{widget.widgetType}</strong>
                      <span className="text-xs text-muted-foreground">#{widget.id}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <CollapsibleDump label="Props" dump={widget.widgetRawData} icon={'table'} align={align} />
                      {widget.widgetError ? (
                        <UnknownErrorDebugCard error={widget.widgetError} />
                      ) : (
                        <CollapsibleBlock label="Rendered Component" icon={'visibility'}>
                          {renderWidget({ widgetType: widget.widgetType!, data: widget.widgetRawData!, index: idx })}
                        </CollapsibleBlock>
                      )}
                    </div>
                  </div>
                ))}
              </CollapsibleBlock>
            )}

            {state.toolCalls.length > 0 && (
              <CollapsibleBlock
                icon={'build'}
                label={`Tool Calls (${state.toolCalls.length})`}
                className="border-t"
                align={align}
              >
                {state.toolCalls.map((toolCall, idx) => (
                  <div key={idx} className="ml-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={toolCall.badgeVariant} className="text-[10px] h-4">
                        {toolCall.extendedStatus}
                      </Badge>
                      {toolCall.humanCallType && (
                        <Badge variant="outline" className="text-[10px] h-4">
                          {toolCall.humanCallType}
                        </Badge>
                      )}
                      <strong>{toolCall.humanToolName}</strong>
                      <span className="text-xs text-muted-foreground">#{toolCall.id}</span>
                    </div>
                    <div className="flex flex-col gap-2 mt-2">
                      {toolCall.agent && (
                        <div className="text-xs text-muted-foreground">
                          Agent: {toolCall.agent.name ?? toolCall.agent.id}
                        </div>
                      )}
                      {toolCall.input !== undefined && (
                        <CollapsibleDump label="Input" dump={toolCall.input} icon={'input'} align={align} />
                      )}
                      {toolCall.output !== undefined && (
                        <CollapsibleDump label="Output" dump={toolCall.output} icon={'output'} align={align} />
                      )}
                      {!!toolCall.error && (
                        <CollapsibleDump label="Error" dump={toolCall.error} icon={'error'} align={align} />
                      )}
                    </div>
                  </div>
                ))}
              </CollapsibleBlock>
            )}

            {resources && resources.length > 0 && (
              <CollapsibleBlock
                icon={'storage'}
                label={`Resource Queries (${resources.length})`}
                initialOpen
                className="border-t"
                align={align}
              >
                {resources.map((resource, idx) => (
                  <ResourceDebugCard key={resource.id ?? idx} resource={resource.resourceMeta} depth={0} />
                ))}
              </CollapsibleBlock>
            )}

            {state.attachments && state.attachments.length > 0 && (
              <CollapsibleBlock
                icon={'attach_file'}
                label={`Attachments (${state.attachments.length})`}
                className="flex items-start gap-2 border-t"
                align={align}
              >
                <div className="font-semibold mb-1">Attachments ({state.attachments.length})</div>
                {state.attachments.map((att, idx) => (
                  <div key={idx} className="ml-2 mb-1">
                    <div>
                      - {att.name} ({att.type})
                    </div>
                    <div className="ml-2 text-muted-foreground">
                      Size: {(att.size / 1024).toFixed(1)}KB
                      {att.url && <div>URL: {att.url.slice(0, 50)}...</div>}
                    </div>
                  </div>
                ))}
              </CollapsibleBlock>
            )}

            <CollapsibleDump
              icon={'sms'}
              label="Chat Message (client-side)"
              dump={msg}
              simplified
              className="pt-2 border-t"
              align={align}
            />
            <CollapsibleDump label="Message UI State" dump={state} icon={'frame_inspect'} />

            {msg.metadata && Object.keys(msg.metadata).length > 0 && (
              <CollapsibleDump
                icon={'info'}
                label="Metadata"
                initialOpen
                simplified
                dump={msg.metadata}
                className="border-t"
                align={align}
              />
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function DebugExternalLinks({ metrics }: { metrics: DebugUsageMetrics }) {
  const hasOpenRouterLink = Boolean(metrics.openRouterGenerationUrl && metrics.openRouterGenerationId);
  const hasMastraLink = Boolean(metrics.mastraStudioUrl);
  if (!hasOpenRouterLink && !hasMastraLink) return null;

  const linkBadge =
    'inline-flex items-center rounded-md border border-border bg-transparent px-1.5 h-5 text-[10px] font-mono no-underline text-foreground cursor-pointer hover:bg-accent';
  return (
    <>
      {hasOpenRouterLink && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <a href={metrics.openRouterGenerationUrl} target="_blank" rel="noreferrer" className={linkBadge}>
                OR
              </a>
            </TooltipTrigger>
            <TooltipContent side="top" align="end" sideOffset={8}>
              <p>OpenRouter generation: {metrics.openRouterGenerationId}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {hasMastraLink && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <a href={metrics.mastraStudioUrl} target="_blank" rel="noreferrer" className={linkBadge}>
                Mastra
              </a>
            </TooltipTrigger>
            <TooltipContent side="top" align="end" sideOffset={8}>
              {metrics.runId && <p>Run: {metrics.runId}</p>}
              {metrics.traceId && <p>Trace: {metrics.traceId}</p>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  );
}

function DebugUsageBadges({ metrics, messageCreatedAt }: { metrics: DebugUsageMetrics; messageCreatedAt: Date }) {
  const exactDateTime = formatExactMessageDateTime(messageCreatedAt);
  return (
    <TooltipProvider delayDuration={200}>
      {metrics.retryCount !== undefined && metrics.retryCount > 0 && (
        <TooltipBadge
          badge={metrics.retryCount}
          variant={'destructive'}
          tooltip={`Retried ${metrics.retryCount} time${metrics.retryCount === 1 ? '' : 's'}: ${metrics.retryReasons?.join(';\n')}`}
        />
      )}
      <TooltipBadge badge={formatMessageTime(messageCreatedAt)} tooltip={exactDateTime} />
      {metrics.cost !== undefined && <TooltipBadge badge={formatUsd(metrics.cost)} />}
      {metrics.durationMs !== undefined && <TooltipBadge badge={formatDuration(metrics.durationMs)} />}
      {metrics.inputTokens !== undefined && (
        <TooltipBadge
          badge={`In ${formatCount(metrics.inputTokens / 1000)}k`}
          tooltip={formatTokenTooltip(metrics.inputTokens)}
        />
      )}
      {metrics.outputTokens !== undefined && (
        <TooltipBadge
          badge={`Out ${formatCount(metrics.outputTokens / 1000)}k`}
          tooltip={formatTokenTooltip(metrics.outputTokens)}
        />
      )}
      {metrics.modelName && (
        <TooltipBadge badge={metrics.modelBadgeName ?? metrics.modelName} tooltip={metrics.modelName} />
      )}
      {metrics.reasoningEffort && (
        <TooltipBadge badge={`${metrics.reasoningEffort} reasoning`} tooltip="Reasoning effort picked in Inspect" />
      )}
      {metrics.promptVersion !== undefined && (
        <TooltipBadge badge={`Prompt v${metrics.promptVersion}`} tooltip="Langfuse prompt version picked in Inspect" />
      )}
      {Object.entries(metrics.skillVersions ?? {}).map(([name, version]) => (
        <TooltipBadge key={name} badge={`${name} v${version}`} tooltip="Langfuse skill version pinned in Inspect" />
      ))}
    </TooltipProvider>
  );
}

function TooltipBadge({
  badge,
  tooltip,
  variant = 'outline',
}: {
  badge: string | number;
  variant?: BadgeVariant;
  tooltip?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={variant} className="h-5 px-1.5 max-w-[100px] truncate text-[10px] font-mono">
          {badge}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" align="end" sideOffset={8}>
        {tooltip ? <p>{tooltip}</p> : null}
      </TooltipContent>
    </Tooltip>
  );
}

function formatCount(value: number | undefined): string {
  if (value === undefined) return '-';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
}

function formatMessageTime(value: Date): string {
  const formatted = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(value);
  return formatted.replace(/\s*([AP]M)$/i, (_, meridiem: string) => meridiem.toLowerCase());
}

function formatExactMessageDateTime(value: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(value);
}

function formatTokenTooltip(value: number): string {
  return `${formatCount(value)} tokens`;
}

function formatDuration(durationMs: number): string {
  return `${(durationMs / 1000).toFixed(1)}s`;
}

function formatUsd(cost: number): string {
  const decimals = cost >= 1 ? 2 : 3;
  const formatted = cost.toFixed(decimals).replace(/\.?0+$/, '');
  return `$${formatted}`;
}

function toNonNegativeCount(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return Math.floor(parsed);
    }
  }
  return undefined;
}

function inferResultCount(result: unknown): number | undefined {
  if (Array.isArray(result)) return result.length;
  if (!result || typeof result !== 'object') return undefined;

  const resultRecord = result as Record<string, unknown>;
  if (Array.isArray(resultRecord.results)) return resultRecord.results.length;
  if (Array.isArray(resultRecord.items)) return resultRecord.items.length;
  if (Array.isArray(resultRecord.result)) return resultRecord.result.length;

  return undefined;
}

function parseDocumentSummaryKeys(documentSummary: unknown): string[] | undefined {
  if (typeof documentSummary !== 'string') return undefined;
  const trimmed = documentSummary.trim();
  const match = trimmed.match(/^Object\((.*)\)$/);
  if (!match) return undefined;

  const inner = match[1]?.trim();
  if (!inner) return [];

  return inner
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

function ResourceDebugCard({ resource, depth }: { resource: ResourceMeta; depth: number }) {
  const nestedResources = extractResourceMeta(resource);
  const {
    id,
    name,
    provider,
    resourceType,
    status,
    resultCount: resultCountRaw,
    resultsCount: resultsCountRaw,
    document,
    documentData,
    documentId,
    collectionPath,
    documentPath,
    pushdownFilters,
    query,
    resolvedQuery,
    metadata,
    ...rest
  } = resource;

  let result = 'result' in resource && resource.result;
  result ||= 'results' in resource && resource.results;
  result ||= 'items' in resource && resource.items;
  const resultCount =
    toNonNegativeCount(resultCountRaw) ?? toNonNegativeCount(resultsCountRaw) ?? inferResultCount(result);
  const resourceName = name ?? id ?? `Resource ${depth}`;
  const displayName = resultCount !== undefined ? `${resourceName} (${resultCount})` : resourceName;
  const documentKeys = !documentData ? parseDocumentSummaryKeys(document) : undefined;
  const resultLabel = resultCount !== undefined ? `Results (${resultCount})` : 'Result';

  const summaryItems = (
    [
      ['Name', displayName],
      ['Provider', provider],
      ['Type', resourceType],
      ['Status', status],
      ['Document ID', documentId],
      ['Document Path', documentPath],
      ['Collection Path', collectionPath],
    ] as const
  ).filter(([, value]) => !isNil(value) && value !== '');

  return (
    <Card className="p-2 bg-background text-[11px] font-mono">
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {summaryItems.map(([label, value]) => (
            <div key={`${label}-${String(value)}`} className="flex items-center gap-1">
              <span className="text-muted-foreground uppercase tracking-wide text-[9px]">{label}:</span>
              <span>{String(value)}</span>
            </div>
          ))}
        </div>

        {!!result && <CollapsibleDump label={resultLabel} dump={result} icon={'output'} />}

        {documentData !== undefined && <CollapsibleDump label="Document" dump={documentData} icon={'description'} />}

        {documentKeys && (
          <CollapsibleDump label={`Document Keys (${documentKeys.length})`} dump={documentKeys} icon={'description'} />
        )}

        {pushdownFilters && (
          <CollapsibleDump
            label={`Pushdown Filters (${pushdownFilters.length})`}
            dump={pushdownFilters}
            icon={'filter_list'}
          />
        )}

        {query && <CollapsibleDump label="Query" dump={query} icon={'query_stats'} />}

        {resolvedQuery && <CollapsibleDump label="Resolved Query" dump={resolvedQuery} icon={'query_stats'} />}

        {metadata && Object.keys(metadata).length > 0 && (
          <CollapsibleDump label="Metadata" dump={metadata} icon={'info'} />
        )}

        {Object.keys(rest).length > 0 && <CollapsibleDump label={`Details`} dump={rest} icon={'info'} />}

        {nestedResources && nestedResources.length > 0 && (
          <CollapsibleBlock label="Nested Resources">
            {nestedResources.map((child, idx) => (
              <ResourceDebugCard key={child.id ?? idx} resource={child} depth={depth + 1} />
            ))}
          </CollapsibleBlock>
        )}
      </div>
    </Card>
  );
}

export function WellFormedErrorDebugCard({ error }: { error: WellFormedError }) {
  return (
    <CollapsibleBlock
      Icon={() => <MaterialIcon icon={'error'} size={IconSizes.xs} className={'text-white'} />}
      className="bg-destructive/70"
      label={
        <div className="flex items-center gap-1 justify-between w-full">
          <div className="flex items-center gap-1">
            <span className=" text-white font-bold">{formatShortToolError(error, { maxLength: 128 })}</span>
            <CopyButton
              content={JSON.stringify(error, null, 2)}
              size={'sm'}
              variant={'ghost'}
              className="text-white hover:bg-white/10"
            />
          </div>
          <ErrorBadge error={error} />
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        <PrettyPre value={error.message} />

        <CollapsibleBlock label="Stack" icon={'code'}>
          <PrettyStack error={error} />
        </CollapsibleBlock>

        {!!error.cause && <CollapsibleDump label="Cause" dump={error.cause} icon={'error'} />}

        {Object.keys(error).length > 0 && <CollapsibleDump label="Details" dump={error} icon={'info'} />}
      </div>
    </CollapsibleBlock>
  );
}

export function ErrorBadge({ error }: { error: WellFormedError }) {
  return (
    <Badge variant="destructive" className="text-[10px] h-4 bg-destructive/50 text-white">
      {error.name}
    </Badge>
  );
}

export function UnknownErrorDebugCard({ error }: { error: unknown }) {
  if (isWellFormedError(error)) {
    return <WellFormedErrorDebugCard error={error} />;
  }
  return <CollapsibleDump label="Unknown Error" dump={error} icon={'error'} />;
}
